import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { communityApi } from 'community/api';
import { websocketCleanupAfterReconnect } from 'modules/webSocket/state/actions';
import { handleQueryError } from 'store/utils';

import {
  attendEventFailedEffect,
  attendEventSuccessEffect,
  deleteEventSuccessEffect,
  deleteEventsFromSeriesSuccessEffect,
  getConnectionsFailedEffect,
  joinCommunityAsMemberFailedEffect,
  refreshEventsList,
  registerForEventFailedEffect,
  registerForEventSuccessEffect,
  resyncActiveEventAfterReconnectEffect,
  retryEventEntryAfterCommunityJoinEffect,
  startEventFailedEffect,
  startEventSuccessEffect,
  stopEventFailedEffect,
} from './effects';
import { eventApi } from './eventApi';
import { eventSeriesApi } from './eventSeriesApi';

import type { Effect, RootState } from 'store';

export const activeEventListener = createListenerMiddleware<RootState>();

activeEventListener.startListening({
  matcher: eventApi.endpoints.deleteEvent.matchFulfilled,
  effect: deleteEventSuccessEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.startEvent.matchRejected,
  effect: startEventFailedEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.startEvent.matchFulfilled,
  effect: startEventSuccessEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.stopEvent.matchRejected,
  effect: stopEventFailedEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.getConnections.matchRejected,
  effect: getConnectionsFailedEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.registerForEvent.matchFulfilled,
  effect: registerForEventSuccessEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.registerForEvent.matchRejected,
  effect: registerForEventFailedEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.attendEvent.matchFulfilled,
  effect: attendEventSuccessEffect,
});

activeEventListener.startListening({
  matcher: eventApi.endpoints.attendEvent.matchRejected,
  effect: attendEventFailedEffect,
});

// Retry a parked event entry the moment the community join succeeds (interactive 403 flow).
activeEventListener.startListening({
  matcher: communityApi.endpoints.joinCommunityAsMember.matchFulfilled,
  effect: retryEventEntryAfterCommunityJoinEffect,
});

// A community join can now fail on its own (it's an explicit mutation post-HNS-5): surface it and
// drop any parked/active entry so the user isn't left stalled without feedback.
activeEventListener.startListening({
  matcher: communityApi.endpoints.joinCommunityAsMember.matchRejected,
  effect: joinCommunityAsMemberFailedEffect,
});

activeEventListener.startListening({
  matcher: isAnyOf(
    eventSeriesApi.endpoints.createEventSeries.matchFulfilled,
    eventSeriesApi.endpoints.deleteEventsFromSeries.matchFulfilled,
    eventSeriesApi.endpoints.convertSingleEventToSeries.matchFulfilled,
    eventSeriesApi.endpoints.updateEventSeries.matchFulfilled
  ),
  effect: refreshEventsList,
});

activeEventListener.startListening({
  matcher: eventSeriesApi.endpoints.deleteEventsFromSeries.matchFulfilled,
  effect: deleteEventsFromSeriesSuccessEffect,
});

// Recover an `event_started`/`event_stopped` frame missed while the socket was down (HNS-238).
activeEventListener.startListening({
  actionCreator: websocketCleanupAfterReconnect,
  effect: resyncActiveEventAfterReconnectEffect,
});

activeEventListener.startListening({
  matcher: isAnyOf(
    eventSeriesApi.endpoints.deleteEventsFromSeries.matchRejected,
    eventApi.endpoints.deleteEvent.matchRejected
  ),
  effect: (({ payload, type }, { dispatch }) => {
    handleQueryError(payload, dispatch, type, 'Unable to delete Event');
  }) satisfies Effect,
});
